import { BASE_MODULE_REGISTRY, type GameDef } from '@kolony/shared';

// The door to the Open Sea's lazy chunk (D276). The classic game never walks through it; a sea
// table must, before anything builds its runtime — the local host's catalog, the networked
// flow's registry and the lobby's thumbnail all read from the chunk this loads.

export type SeaMode = typeof import('./seaMode');

const realImport = (): Promise<SeaMode> => import('./seaMode');

let importer: () => Promise<SeaMode> = realImport;
let loaded: SeaMode | null = null;
let pending: Promise<SeaMode> | null = null;

/** The sea chunk if it has already arrived — for code that must decide synchronously. */
export function seaModeNow(): SeaMode | null {
  return loaded;
}

/**
 * Fetch the sea chunk once. A failure is EVICTED, never remembered: the network that failed
 * may be back by the next ask, and a pinned rejection would make every later sea table fail
 * for the rest of the session (D203's rule for the parsed-asset memo, applied to code).
 */
export function loadSeaMode(): Promise<SeaMode> {
  if (loaded !== null) return Promise.resolve(loaded);
  pending ??= importer().then(
    (mode) => {
      loaded = mode;
      return mode;
    },
    (err: unknown) => {
      pending = null;
      throw err;
    },
  );
  return pending;
}

/**
 * Does building this def's runtime need the sea chunk? Content-blind by construction: a def
 * naming any module the BASE registry lacks cannot be composed from the classic catalog —
 * which is also how a pasted custom map with a sea block is caught, not just a named scenario.
 */
export const needsSeaModules = (def: Pick<GameDef, 'modules'>): boolean => def.modules.some((id) => !BASE_MODULE_REGISTRY.has(id));

/** Test seam: forget whatever was loaded — and optionally stand in for the chunk's import. */
export function resetSeaModeForTests(next: () => Promise<SeaMode> = realImport): void {
  importer = next;
  loaded = null;
  pending = null;
}
